import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule } from '@angular/core';
import { OrderFlowErrorHandler } from './core/services/telemetry/order-flow-error-handler';
import { ApiFailInterceptor } from './core/services/telemetry/api-fail.interceptor';
import { SentryService } from './core/services/telemetry/sentry/sentry.service';
import { OpenReplayService } from './core/services/telemetry/openreplay/openreplay.service';
import { GravacoesService } from './core/services/telemetry/gravacoes/gravacoes.service';
import { LegacyFloatingLabelService } from './shared/legacy-floating-label.service';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { DefaultUrlSerializer, UrlSerializer, UrlTree } from '@angular/router';

@Injectable()
class RestaurantUrlSerializer extends DefaultUrlSerializer {
    parse(url: string): UrlTree {
        const parts = url.split('?');
        const segments = parts[0].split('/');
        if (segments.length > 1) segments[1] = segments[1].toLowerCase();
        parts[0] = segments.join('/');
        return super.parse(parts.join('?'));
    }
}

import { IonicModule } from '@ionic/angular';

import { CoreModule } from './core/core.module';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { TokenInterceptor } from './core/interceptor/token.interceptor';
import { MenuPageModule } from './pages/menu/menu.module';
import { AppMaskModule } from './core/mask/app-mask.module'
import { UntypedFormBuilder } from '@angular/forms';
import { MpImageViewerModule } from 'src/app/shared/components/image-viewer/image-viewer.module';

import { PipesModule } from './shared/pipe/pipe.module';
import { NotesDialogPageModule } from './pages/shopping-cart/notes-dialog/notes-dialog.module';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { RedirectWhatsappPageModule } from './pages/payment/redirect-whatsapp/redirect-whatsapp.module';
import { FollowOrderPageModule } from './pages/payment/follow-order/follow-order.module';
import { CvvCardPageModule } from './pages/credit-cards/cvv-card/cvv-card.module';
import { TimeoutPageModule } from './pages/timeout/timeout.module';
import { ModalOutletComponent } from './shared/components/modal-outlet/modal-outlet.component';
import { LoginPageModule } from './pages/login/login.module';
import { ModalPageModule } from './pages/modal/modal.module';

@NgModule({
  declarations: [
    AppComponent,
    ModalOutletComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    IonicModule.forRoot({scrollAssist: true, scrollPadding: true}),
    AppRoutingModule,

    CoreModule,

    MenuPageModule,
    CvvCardPageModule,
    TimeoutPageModule,
    NotesDialogPageModule,

    AppMaskModule,
    MpImageViewerModule,
    RedirectWhatsappPageModule,
    FollowOrderPageModule,
    LoginPageModule,
    ModalPageModule,

  ],
  providers: [
    { provide: UrlSerializer, useClass: RestaurantUrlSerializer },
    TranslateModule,
    CurrencyPipe,
    UntypedFormBuilder,
    PipesModule,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    // Telemetria de saúde (health_15m → alertas js_error/api_fail no
    // order-flow-monitor). O handler delega o console.error default; o
    // interceptor só observa falhas (0/5xx/timeout) de endpoints críticos.
    {
      provide: ErrorHandler,
      useClass: OrderFlowErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiFailInterceptor,
      multi: true
    },
    // Compat Ionic 7+: restaura a animação do floating/stacked label no pattern legado
    // <ion-item><ion-label position><ion-input> — o <ion-item> parou de rastrear foco/valor
    // do controle aninhado. Um único registro global de listeners. Ver LegacyFloatingLabelService.
    {
      provide: APP_INITIALIZER,
      useFactory: (svc: LegacyFloatingLabelService) => () => svc.init(),
      deps: [LegacyFloatingLabelService],
      multi: true
    },
    // Sentry: o initializer só DECIDE e AGENDA (síncrono, sem promise — não
    // atrasa o bootstrap em nada). O SDK entra por chunk lazy, no primeiro idle
    // depois do `load`, e só nos devices dentro da coorte. Ver SENTRY.md.
    {
      provide: APP_INITIALIZER,
      useFactory: (svc: SentryService) => () => svc.boot(),
      deps: [SentryService],
      multi: true
    },
    // OpenReplay: mesma forma do Sentry — o initializer só DECIDE e AGENDA
    // (síncrono, sem promise). O tracker entra por chunk lazy no primeiro idle
    // depois do `load`, e o gate de LOJA é reavaliado lá, porque só naquele
    // instante a loja existe. Ver OPENREPLAY.md.
    {
      provide: APP_INITIALIZER,
      useFactory: (svc: OpenReplayService) => () => svc.boot(),
      deps: [OpenReplayService],
      multi: true
    },
    // Gravações (rrweb): sucedem o OpenReplay, mesma forma — o initializer só
    // DECIDE e AGENDA; o gravador entra por chunk lazy no idle após o `load`,
    // com o gate de loja reavaliado lá. Ver GRAVACOES.md.
    {
      provide: APP_INITIALIZER,
      useFactory: (svc: GravacoesService) => () => svc.boot(),
      deps: [GravacoesService],
      multi: true
    }
  ],
  exports: [
    TranslateModule,
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
